/**
 * The Business Builder Course as the prototype writes it: the course's
 * heading, and per week its title, summary, outcome, the NOVA assets it
 * builds, and the templates it hands out. Admins edit the course once it is
 * in the database; this is only what it starts from.
 *
 * Read by `src/scripts/seed-business-builder-course.ts`, which creates the
 * course, and by `e2e/seed/course-fixtures.ts`, which seeds it for screenshots.
 *
 * Aliases: business builder outline, course outline, prototype weeks
 */

import type { AssetKey } from "~/lib/shared/nova/assets/catalog";

export const BUSINESS_BUILDER_COURSE = {
  slug: "business-builder-program",
  title: "Business Builder Program",
  headline: "Build your Client Acquisition System",
  description:
    "A video program that teaches you to build your own client acquisition system, included in NOVA. Follow it on your own, or work through the same eight weeks with a coach.",
} as const;

/** The Accelerator runs alongside this course, and only this page shows its card. */
export const hasAcceleratorCard = (slug: string): boolean =>
  slug === BUSINESS_BUILDER_COURSE.slug;

export interface BusinessBuilderWeek {
  readonly title: string;
  readonly summary: string;
  readonly outcome: string;
  readonly producesAssets: readonly AssetKey[];
  /** Template names from the prototype. Their links are added by an admin. */
  readonly templates: readonly string[];
}

export const BUSINESS_BUILDER_WEEKS: readonly BusinessBuilderWeek[] = [
  {
    title: "Your Ideal client and positioning",
    summary:
      "One person, the trigger problem, and the line that says who you are for.",
    outcome:
      "Your ideal client, the ten people you can reach, and the line that says who you are for.",
    producesAssets: [
      "identity_statement",
      "ideal_client_profile",
      "niche_selection",
    ],
    templates: [
      "Ideal client canvas",
      "Ten-contact list",
      "Positioning statement worksheet",
    ],
  },
  {
    title: "Your core offer",
    summary: "Structure, price and the one sentence that makes it obvious.",
    outcome: "One core offer you can say in a sentence, priced to your hours.",
    producesAssets: ["core_offer"],
    templates: [
      "Offer structure worksheet",
      "Pricing sanity check",
      "One-sentence offer script",
    ],
  },
  {
    title: "Mini-Sprint design",
    summary: "The smallest thing a stranger can say yes to, and how to run it.",
    outcome: "A five-day sprint a stranger can say yes to.",
    producesAssets: ["mini_sprint"],
    templates: ["Five-day sprint outline", "Sprint email skeletons"],
  },
  {
    title: "LinkedIn optimization",
    summary:
      "A profile that reads like your offer, so the traffic you already have converts.",
    outcome: "A LinkedIn profile that reads like your offer, with proof in it.",
    producesAssets: ["dcm_profile", "linkedin_profile"],
    templates: [
      "Profile rewrite checklist",
      "Headline and about bank",
      "Proof section template",
    ],
  },
  {
    title: "LinkedIn content and outreach",
    summary: "What to post, who to message, and what to say when they reply.",
    outcome: "A week of posts and ten invitations sent, in your voice.",
    producesAssets: ["outreach_scripts"],
    templates: [
      "Weekly post planner",
      "Warm invitation script",
      "Reply handling one-pager",
      "Four-day follow-up",
    ],
  },
  {
    title: "Conversations that convert",
    summary: "The discovery call, the price moment, and what to do with a no.",
    outcome:
      "A discovery call you are not dreading, and a price you can say out loud.",
    producesAssets: [],
    templates: [
      "Discovery call outline",
      "Price conversation script",
      "What to do with a no",
    ],
  },
  {
    title: "Proof: stories and profiles",
    summary: "Turning your own sessions into the evidence a stranger needs.",
    outcome: "Two client stories and a profile with proof in it.",
    producesAssets: [],
    templates: ["Client story template", "Profile proof section"],
  },
  {
    title: "Your 90 days, and what to change",
    summary: "Reading your own numbers and deciding the next move.",
    outcome: "A ninety-day plan you wrote from your own numbers.",
    producesAssets: ["ninety_day_plan"],
    templates: ["Ninety-day review sheet", "Numbers dashboard"],
  },
];
